import { useQuery, UseQueryOptions } from '@tanstack/react-query';

import { axiosHelper } from '@setvi/shared/services';
import { AxiosMethods, PricingApi } from '@setvi/shared/enums';

import {
  PricingConfigurationDTO,
  PricingModule,
  PricingModuleSettings,
  PricingSettings,
  PricingSettingsSource
} from './Types';

export * from './Types';

export enum PricingQueryKey {
  GET_PRICING_CONFIGURATION = 'GET_PRICING_CONFIGURATION'
}

/**
 * Safe pre-load default: assume no pricing until the real configuration
 * arrives, so price UI never flashes for a NO_PRICING client. `costAvailable`
 * follows the same rule for the same reason (DEV-795) — a cost or a GP shown
 * for one frame and then withdrawn is the one outcome that flag exists to
 * prevent, and hiding a column that then appears is the recoverable direction.
 */
export const DEFAULT_PRICING_SETTINGS: PricingSettings = {
  mode: 'NO_PRICING',
  defaultMarginEnabled: false,
  defaultMarginPercent: 0,
  costAvailable: false
};

export const fetchPricingConfiguration = (): Promise<PricingConfigurationDTO> =>
  axiosHelper({
    endpoint: PricingApi.Configuration,
    method: AxiosMethods.GET,
    baseURL: process.env.REVOPS_API
  });

/**
 * The document only changes when support flips a switch via
 * `PATCH /pricing/configuration`, so it is effectively static within a session.
 * Without this, every new subscriber (each quote row, the orders table, the
 * freehand dialog) would trigger a background revalidation on mount.
 */
const PRICING_CONFIGURATION_STALE_TIME = 5 * 60 * 1000;

export const getPricingConfigurationQuery =
  (): UseQueryOptions<PricingConfigurationDTO> => ({
    queryKey: [PricingQueryKey.GET_PRICING_CONFIGURATION],
    queryFn: fetchPricingConfiguration,
    staleTime: PRICING_CONFIGURATION_STALE_TIME
  });

/**
 * Narrow a pricing-configuration document (or the legacy nested pricing object)
 * down to the settings the UI reads. Returns null for anything unusable so
 * callers can fall back instead of silently treating it as "pricing enabled".
 */
export const toPricingSettings = (
  source?: PricingSettingsSource | null
): PricingSettings | null => {
  if (!source?.mode) return null;

  // Absent reads as `true`: the flag is new (DEV-795), and the legacy nested
  // `quoteConfiguration.pricing` block this also parses will never carry it.
  // Every tenant but Lindenmeyr / CNG has cost.
  const costAvailable = source.costAvailable ?? true;

  return {
    mode: source.mode as PricingSettings['mode'],
    // With no cost there is nothing to apply a margin to, so the backend
    // forces this off for such a tenant. Mirrored here so the UI agrees even
    // when the value reaches it from the legacy block, which is not forced.
    defaultMarginEnabled: costAvailable
      ? (source.defaultMarginEnabled ?? false)
      : false,
    defaultMarginPercent: source.defaultMarginPercent ?? 0,
    costAvailable
  };
};

/**
 * Read the pricing configuration from its own document, falling back to the
 * legacy nested value while the backend change (DEV-540) rolls out. Used by the
 * config stores; components read `pricing` off those stores as before.
 */
export const fetchPricingSettings = async (
  fallback?: PricingSettingsSource | null
): Promise<PricingSettings> => {
  try {
    const configuration = await fetchPricingConfiguration();
    const pricing = toPricingSettings(configuration);

    if (pricing) return pricing;
  } catch {
    // /pricing/configuration not deployed yet — use the value the quote
    // configuration still carries.
  }

  return toPricingSettings(fallback) ?? DEFAULT_PRICING_SETTINGS;
};

export const usePricingConfiguration = ({
  enabled = true
}: { enabled?: boolean } = {}) => {
  const {
    data: pricingConfiguration,
    error,
    isFetching
  } = useQuery({ ...getPricingConfigurationQuery(), enabled });

  return {
    pricingConfiguration,
    pricing:
      toPricingSettings(pricingConfiguration) ?? DEFAULT_PRICING_SETTINGS,
    error,
    isFetching
  };
};

/**
 * Whether this tenant has cost data at all (DEV-795).
 *
 * `false` ⇒ the backend omits every cost / GP / margin key from its responses,
 * so the UI must drop those columns, inputs and totals outright. It must NOT
 * fall back to computing them, which would render `$0.00` and `100%` from the
 * missing values.
 *
 * Reads `false` while the configuration is still in flight — see
 * DEFAULT_PRICING_SETTINGS. `isFetching` is exposed for callers that would
 * rather wait than swap a column in.
 */
export const useCostAvailable = () => {
  const { pricing, isFetching } = usePricingConfiguration();

  return { costAvailable: pricing.costAvailable, isFetching };
};

/**
 * Used before the configuration has loaded, and for a backend that does not
 * send `modules` yet. `alwaysEditable: false` means "trust the item's own
 * `isEditable`" — the behavior these screens had before DEV-540 — so a
 * contract-locked price is never briefly editable while the document is in
 * flight.
 */
export const DEFAULT_PRICING_MODULE_SETTINGS: PricingModuleSettings = {
  cache: false,
  alwaysEditable: false
};

/**
 * Read one module's switches from `pricing-configuration` (DEV-540).
 *
 * `alwaysEditable` is a FALLBACK, not an override: the item's own `isEditable`
 * has priority wherever the two disagree. `isEditable === false` is a real lock
 * from the pricing source — a contract price, a locked catalog item — and this
 * switch must not undo it; `true` was already editable. So the switch only
 * decides for items that carry NO flag at all.
 *
 * That's still what makes flipping it take effect without waiting for a
 * re-price, since the backend applies its own rule when it prices an item and
 * items priced before the flip carry the flag from that pass.
 */
export const usePricingModuleSettings = (module: PricingModule) => {
  const { pricingConfiguration, isFetching } = usePricingConfiguration();
  const settings = pricingConfiguration?.modules?.[module];

  return {
    cache: settings?.cache ?? DEFAULT_PRICING_MODULE_SETTINGS.cache,
    alwaysEditable:
      settings?.alwaysEditable ??
      DEFAULT_PRICING_MODULE_SETTINGS.alwaysEditable,
    isFetching
  };
};
